import React, { useState, useEffect } from 'react';
import { Card, Button, Spin, Alert, Tabs, Typography, Space, Tag, Rate, Progress } from 'antd';
import { 
    ShopOutlined, 
    StarOutlined, 
    ClockCircleOutlined, 
    DollarOutlined,
    TeamOutlined,
    TrophyOutlined,
    RobotOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import aiService from '../../services/aiService';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const AIVendorAnalysis = ({ 
    vendor, 
    visible = false, 
    onClose,
    className = ""
}) => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [analysis, setAnalysis] = useState({
        overview: null,
        strengths: null,
        improvements: null,
        comparison: null,
        trends: null
    });
    const [error, setError] = useState(null);

    useEffect(() => {
        if (visible && vendor) {
            loadVendorAnalysis();
        }
    }, [visible, vendor]);

    const loadVendorAnalysis = async () => {
        if (!vendor) return;

        setLoading(true);
        setError(null);

        try {
            // Load overview analysis
            const overviewResult = await analyzeVendorOverview(vendor);
            if (overviewResult.success) {
                setAnalysis(prev => ({ ...prev, overview: overviewResult.data }));
            }

            // Load other analyses in parallel
            const [strengthsResult, improvementsResult, comparisonResult, trendsResult] = await Promise.all([
                analyzeVendorStrengths(vendor),
                analyzeVendorImprovements(vendor),
                analyzeVendorComparison(vendor),
                analyzeVendorTrends(vendor)
            ]);

            if (strengthsResult.success) {
                setAnalysis(prev => ({ ...prev, strengths: strengthsResult.data }));
            }
            if (improvementsResult.success) {
                setAnalysis(prev => ({ ...prev, improvements: improvementsResult.data }));
            }
            if (comparisonResult.success) {
                setAnalysis(prev => ({ ...prev, comparison: comparisonResult.data }));
            }
            if (trendsResult.success) {
                setAnalysis(prev => ({ ...prev, trends: trendsResult.data }));
            }

        } catch (error) {
            console.error('Vendor analysis error:', error);
            setError('Failed to load vendor analysis');
        } finally {
            setLoading(false);
        }
    };

    const analyzeVendorOverview = async (vendor) => {
        const prompt = `Analyze this food vendor and provide a comprehensive overview:

Vendor: ${vendor.business_name}
Description: ${vendor.description || 'No description provided'}
Cuisines: ${vendor.cuisines ? vendor.cuisines.join(', ') : 'Not specified'}
Rating: ${vendor.rating || 'Not available'}
Total Reviews: ${vendor.total_reviews || 0}
Location: ${vendor.city || 'Not specified'}
Years in Business: ${vendor.years_in_business || 'Unknown'}

Please provide:
1. Business summary and positioning
2. Target customer analysis
3. Market positioning
4. Key differentiators
5. Overall business health assessment

Return as JSON with detailed analysis.`;

        return await aiService.chatWithAI(prompt, { type: 'vendor_overview' });
    };

    const analyzeVendorStrengths = async (vendor) => {
        const prompt = `Identify the key strengths of this food vendor:

Vendor: ${vendor.business_name}
Rating: ${vendor.rating || 'Not available'}
Reviews: ${vendor.total_reviews || 0}
Cuisines: ${vendor.cuisines ? vendor.cuisines.join(', ') : 'Not specified'}
Description: ${vendor.description || 'No description provided'}

Please identify:
1. Top 5 strengths
2. Competitive advantages
3. Unique selling points
4. Customer satisfaction factors
5. Operational strengths

Return as JSON with strengths analysis.`;

        return await aiService.chatWithAI(prompt, { type: 'vendor_strengths' });
    };

    const analyzeVendorImprovements = async (vendor) => {
        const prompt = `Suggest improvements for this food vendor:

Vendor: ${vendor.business_name}
Rating: ${vendor.rating || 'Not available'}
Reviews: ${vendor.total_reviews || 0}
Cuisines: ${vendor.cuisines ? vendor.cuisines.join(', ') : 'Not specified'}

Please suggest:
1. Top 5 improvement areas
2. Customer experience enhancements
3. Operational improvements
4. Marketing opportunities
5. Growth strategies

Return as JSON with improvement suggestions.`;

        return await aiService.chatWithAI(prompt, { type: 'vendor_improvements' });
    };

    const analyzeVendorComparison = async (vendor) => {
        const prompt = `Compare this vendor with industry standards:

Vendor: ${vendor.business_name}
Rating: ${vendor.rating || 'Not available'}
Reviews: ${vendor.total_reviews || 0}
Cuisines: ${vendor.cuisines ? vendor.cuisines.join(', ') : 'Not specified'}

Please provide:
1. Industry benchmark comparison
2. Performance metrics analysis
3. Competitive positioning
4. Market share potential
5. Growth opportunities

Return as JSON with comparison analysis.`;

        return await aiService.chatWithAI(prompt, { type: 'vendor_comparison' });
    };

    const analyzeVendorTrends = async (vendor) => {
        const prompt = `Analyze trends and future outlook for this vendor:

Vendor: ${vendor.business_name}
Cuisines: ${vendor.cuisines ? vendor.cuisines.join(', ') : 'Not specified'}
Location: ${vendor.city || 'Not specified'}

Please analyze:
1. Market trends affecting this vendor
2. Future growth potential
3. Emerging opportunities
4. Potential challenges
5. Strategic recommendations

Return as JSON with trends analysis.`;

        return await aiService.chatWithAI(prompt, { type: 'vendor_trends' });
    };

    const parseAIResponse = (response) => {
        try {
            // Remove markdown code block formatting if present
            let cleanResponse = response.trim();
            
            // Remove ```json and ``` if present
            if (cleanResponse.startsWith('```json')) {
                cleanResponse = cleanResponse.replace(/^```json\s*/, '').replace(/\s*```$/, '');
            } else if (cleanResponse.startsWith('```')) {
                cleanResponse = cleanResponse.replace(/^```\s*/, '').replace(/\s*```$/, '');
            }
            
            // Try to parse as JSON
            return JSON.parse(cleanResponse);
        } catch (error) {
            console.error('Failed to parse AI response:', error);
            console.log('Raw response:', response);
            
            // If parsing fails, try to extract JSON-like content
            try {
                // Look for JSON-like patterns in the response
                const jsonMatch = response.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    return JSON.parse(jsonMatch[0]);
                }
                
                // Look for array-like patterns
                const arrayMatch = response.match(/\[[\s\S]*\]/);
                if (arrayMatch) {
                    return JSON.parse(arrayMatch[0]);
                }
            } catch (secondError) {
                console.error('Secondary parsing also failed:', secondError);
            }
            
            // Return null if all parsing attempts fail
            return null;
        }
    };

    const renderOverview = () => {
        if (!analysis.overview) return <Spin />;

        try {
            const data = typeof analysis.overview === 'string' ? parseAIResponse(analysis.overview) : analysis.overview;
            
            return (
                <div className="space-y-4">
                    <Card title="Business Summary" size="small">
                        <Paragraph>{data.business_summary || 'Analysis not available'}</Paragraph>
                    </Card>
                    
                    <Card title="Target Customers" size="small">
                        <Paragraph>{data.target_customers || 'Analysis not available'}</Paragraph>
                    </Card>
                    
                    <Card title="Market Positioning" size="small">
                        <Paragraph>{data.market_positioning || 'Analysis not available'}</Paragraph>
                    </Card>
                    
                    <Card title="Key Differentiators" size="small">
                        <ul className="list-disc list-inside space-y-1">
                            {data.key_differentiators?.map((item, index) => (
                                <li key={index}>{item}</li>
                            )) || <li>Analysis not available</li>}
                        </ul>
                    </Card>
                </div>
            );
        } catch (error) {
            return <Alert message="Failed to parse overview analysis" type="error" />;
        }
    };

    const renderStrengths = () => {
        if (!analysis.strengths) return <Spin />;

        try {
            const data = typeof analysis.strengths === 'string' ? parseAIResponse(analysis.strengths) : analysis.strengths;
            
            return (
                <div className="space-y-4">
                    <Card title="Top Strengths" size="small">
                        <div className="space-y-2">
                            {data.top_strengths?.map((strength, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <TrophyOutlined className="text-yellow-500" />
                                    <Text>{strength}</Text>
                                </div>
                            )) || <Text>Analysis not available</Text>}
                        </div>
                    </Card>
                    
                    <Card title="Competitive Advantages" size="small">
                        <div className="space-y-2">
                            {data.competitive_advantages?.map((advantage, index) => (
                                <Tag key={index} color="green">{advantage}</Tag>
                            )) || <Text>Analysis not available</Text>}
                        </div>
                    </Card>
                    
                    <Card title="Customer Satisfaction Factors" size="small">
                        <Paragraph>{data.customer_satisfaction || 'Analysis not available'}</Paragraph>
                    </Card>
                </div>
            );
        } catch (error) {
            return <Alert message="Failed to parse strengths analysis" type="error" />;
        }
    };

    const renderImprovements = () => {
        if (!analysis.improvements) return <Spin />;

        try {
            const data = typeof analysis.improvements === 'string' ? parseAIResponse(analysis.improvements) : analysis.improvements;
            
            return (
                <div className="space-y-4">
                    <Card title="Improvement Areas" size="small">
                        <div className="space-y-2">
                            {data.improvement_areas?.map((area, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <BarChartOutlined className="text-blue-500" />
                                    <Text>{area}</Text>
                                </div>
                            )) || <Text>Analysis not available</Text>}
                        </div>
                    </Card>
                    
                    <Card title="Customer Experience Enhancements" size="small">
                        <Paragraph>{data.customer_experience || 'Analysis not available'}</Paragraph>
                    </Card>
                    
                    <Card title="Growth Strategies" size="small">
                        <div className="space-y-2">
                            {data.growth_strategies?.map((strategy, index) => (
                                <Tag key={index} color="blue">{strategy}</Tag>
                            )) || <Text>Analysis not available</Text>}
                        </div>
                    </Card>
                </div>
            );
        } catch (error) {
            return <Alert message="Failed to parse improvements analysis" type="error" />;
        }
    };

    const renderComparison = () => {
        if (!analysis.comparison) return <Spin />;

        try {
            const data = typeof analysis.comparison === 'string' ? parseAIResponse(analysis.comparison) : analysis.comparison;
            
            return (
                <div className="space-y-4">
                    <Card title="Industry Benchmarks" size="small">
                        <div className="space-y-3">
                            <div>
                                <Text strong>Rating Performance</Text>
                                <Progress 
                                    percent={((vendor.rating || 0) / 5) * 100} 
                                    format={() => `${vendor.rating || 0}/5`}
                                />
                            </div>
                            <div>
                                <Text strong>Review Volume</Text>
                                <Text className="ml-2">{vendor.total_reviews || 0} reviews</Text>
                            </div>
                        </div>
                    </Card>
                    
                    <Card title="Competitive Positioning" size="small">
                        <Paragraph>{data.competitive_positioning || 'Analysis not available'}</Paragraph>
                    </Card>
                    
                    <Card title="Market Share Potential" size="small">
                        <Paragraph>{data.market_share_potential || 'Analysis not available'}</Paragraph>
                    </Card>
                </div>
            );
        } catch (error) {
            return <Alert message="Failed to parse comparison analysis" type="error" />;
        }
    };

    const renderTrends = () => {
        if (!analysis.trends) return <Spin />;

        try {
            const data = typeof analysis.trends === 'string' ? parseAIResponse(analysis.trends) : analysis.trends;
            
            return (
                <div className="space-y-4">
                    <Card title="Market Trends" size="small">
                        <Paragraph>{data.market_trends || 'Analysis not available'}</Paragraph>
                    </Card>
                    
                    <Card title="Growth Potential" size="small">
                        <Paragraph>{data.growth_potential || 'Analysis not available'}</Paragraph>
                    </Card>
                    
                    <Card title="Strategic Recommendations" size="small">
                        <div className="space-y-2">
                            {data.strategic_recommendations?.map((rec, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <StarOutlined className="text-purple-500" />
                                    <Text>{rec}</Text>
                                </div>
                            )) || <Text>Analysis not available</Text>}
                        </div>
                    </Card>
                </div>
            );
        } catch (error) {
            return <Alert message="Failed to parse trends analysis" type="error" />;
        }
    };

    if (!visible) return null;

    return (
        <Card
            className={`w-full max-w-4xl ${className}`}
            title={
                <div className="flex items-center gap-2">
                    <RobotOutlined className="text-blue-500" />
                    <span>AI Vendor Analysis</span>
                    <Text className="text-gray-500">- {vendor?.business_name}</Text>
                </div>
            }
            extra={
                onClose && (
                    <Button type="text" onClick={onClose}>
                        Close
                    </Button>
                )
            }
        >
            {error && (
                <Alert
                    message={error}
                    type="error"
                    showIcon
                    className="mb-4"
                />
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Overview" key="overview">
                    {loading ? <Spin /> : renderOverview()}
                </TabPane>
                <TabPane tab="Strengths" key="strengths">
                    {loading ? <Spin /> : renderStrengths()}
                </TabPane>
                <TabPane tab="Improvements" key="improvements">
                    {loading ? <Spin /> : renderImprovements()}
                </TabPane>
                <TabPane tab="Comparison" key="comparison">
                    {loading ? <Spin /> : renderComparison()}
                </TabPane>
                <TabPane tab="Trends" key="trends">
                    {loading ? <Spin /> : renderTrends()}
                </TabPane>
            </Tabs>
        </Card>
    );
};

export default AIVendorAnalysis;
